// ADR-0009 Peça 6 — o CONTRATO de wire do liveness, em cópia única.
//
// Extraído em 24/08 junto com o `LivenessCapture`, quando o web-admin passou a exigir
// reconhecimento facial. O componente já era compartilhado; faltava isto, e sem isto a
// extração seria meia: cada app teria a sua tradução de `resp.status` → `LivenessOutcome`,
// e uma mudança de contrato no servidor (um status novo, um `error` novo) seria corrigida
// num app e esquecida no outro. O sintoma seria o pior tipo — o admin lendo `invalid_capture`
// onde o comitê lê `no_template`, com a tela mandando a pessoa fazer a coisa errada.
//
// O que NÃO vem para cá, de propósito: o `fetch`. Cada app tem o seu `apiFetch`, com o seu
// storage de sessão e o seu tratamento de 401 — transporte é do app, tradução é do contrato.

import type { ChallengeResult, LivenessOutcome } from '../components/LivenessCapture';

/** Traduz a resposta de POST /liveness/challenge. */
export async function parseChallengeResponse(resp: Response): Promise<ChallengeResult> {
  if (!resp.ok) return { ok: false, error: `http_${String(resp.status)}` };
  const data = (await resp.json().catch(() => ({}))) as {
    challengeToken?: string;
    direction?: 'direita' | 'esquerda';
    expiresInSeconds?: number;
  };
  if (!data.challengeToken || !data.direction) return { ok: false, error: 'bad_response' };
  return {
    ok: true,
    challengeToken: data.challengeToken,
    direction: data.direction,
    ...(data.expiresInSeconds !== undefined ? { expiresInSeconds: data.expiresInSeconds } : {}),
  };
}

/**
 * Traduz a resposta de POST /liveness/verify.
 *
 * `aplicarTokenElevado` recebe o token novo em `liberar`. O servidor também manda
 * `X-Session-Refresh` (e o `apiFetch` dos dois apps já o aplica); reforçar pelo body é
 * determinismo, não redundância inútil — foi o que fez o comitê funcionar quando o header
 * se perdeu em algum proxy.
 */
export async function parseVerifyResponse(
  resp: Response,
  aplicarTokenElevado: (token: string) => void,
): Promise<LivenessOutcome> {
  if (resp.status === 423) return { kind: 'locked' };
  if (resp.status === 503) return { kind: 'degraded' };
  if (resp.status === 422) {
    const b = (await resp.json().catch(() => ({}))) as { error?: string };
    return b.error === 'no_template_enrolled'
      ? { kind: 'no_template' }
      : { kind: 'invalid_capture' };
  }
  if (resp.status === 400) {
    const b = (await resp.json().catch(() => ({}))) as { error?: string };
    return b.error === 'challenge_invalid'
      ? { kind: 'challenge_invalid' }
      : { kind: 'error', error: b.error ?? 'bad_request' };
  }
  if (!resp.ok) return { kind: 'error', error: `http_${String(resp.status)}` };

  const data = (await resp.json().catch(() => ({}))) as {
    decision?: string;
    reason?: string;
    attemptsRemaining?: number;
    token?: string;
  };
  if (data.decision === 'liberar') {
    if (data.token) aplicarTokenElevado(data.token);
    return { kind: 'liberar' };
  }
  if (data.decision === 'negar') {
    return {
      kind: 'negar',
      reason: data.reason ?? 'rosto_nao_corresponde',
      attemptsRemaining: data.attemptsRemaining ?? 0,
    };
  }
  return { kind: 'error', error: 'unexpected' };
}

/** Classifica a rejeição do fetch — abort (cold start longo) vs rede. */
export function erroDeTransporte(e: unknown): LivenessOutcome {
  const aborted = e instanceof Error && e.name === 'AbortError';
  return { kind: 'error', error: aborted ? 'timeout' : 'network' };
}
